import type { CountryCode } from '@constants'
import { datadogRum } from '@datadog/browser-rum'
import { useApiECommercePaymentExecution } from '@hooks-api/eCommerceV2/useApiECommercePaymentExecution'
import { queryStringify, wordPressHost } from '@utils'
import { useEffect, useState } from 'react'
import * as paymentService from '../core/paymentService'

/** Persists payment_option_id across the CKO Flow redirect APM flow (checkout → thank-you page). */
export const CKO_REDIRECT_APM_OPTION_ID_KEY = 'cko_redirect_apm_payment_option_id'

interface UseCkoRedirectApmExecutionArgs {
  ckoPaymentId: string | null | undefined
  paymentTransactionId: string | undefined
  orderHashFromUrl: string | null | undefined
  countryCode: CountryCode
}

export const useCkoRedirectApmExecution = ({ ckoPaymentId, paymentTransactionId, orderHashFromUrl, countryCode }: UseCkoRedirectApmExecutionArgs) => {
  const { trigger: executePayment } = useApiECommercePaymentExecution()
  const [isCkoExecuting, setIsCkoExecuting] = useState(
    () => !!ckoPaymentId && !!sessionStorage.getItem(CKO_REDIRECT_APM_OPTION_ID_KEY),
  )

  useEffect(function executeCkoRedirectApmPayment() {
    if (!ckoPaymentId) return
    const storedOptionId = sessionStorage.getItem(CKO_REDIRECT_APM_OPTION_ID_KEY)
    if (!storedOptionId) return

    const paymentSuccessUrl = `${wordPressHost}/checkout/order-received/${paymentTransactionId}/?${queryStringify({ orderHash: orderHashFromUrl })}`
    const paymentFallbackUrl = `${wordPressHost}/checkout/order-pay/${paymentTransactionId}/?${queryStringify({ orderHash: orderHashFromUrl, paymentFailed: true })}`

    paymentService.executeWithCkoReconciliationRetry(() => executePayment({
      country_code: countryCode,
      payment_transaction_id: paymentTransactionId,
      service_pay_info: { bank_code: null, apple_pay_info: null, pay_token: null },
      user_payment_token_info: null,
      payment_success_url: paymentSuccessUrl,
      payment_fallback_url: paymentFallbackUrl,
      payment_option_id: Number(storedOptionId),
      cko_payment_id: ckoPaymentId,
    })).then((executionResponse) => {
      sessionStorage.removeItem(CKO_REDIRECT_APM_OPTION_ID_KEY)
      const redirectUrl = executionResponse?.data?.redirect_url
      if (redirectUrl) {
        window.location.href = redirectUrl
      }
      else {
        setIsCkoExecuting(false)
      }
    }).catch((executionError: unknown) => {
      datadogRum.addError(new Error('[useCkoRedirectApmExecution] /payment/execution failed'), {
        function: 'useCkoRedirectApmExecution.executePayment',
        paymentTransactionId,
        ckoPaymentId,
        error: executionError,
      })
      sessionStorage.removeItem(CKO_REDIRECT_APM_OPTION_ID_KEY)
      setIsCkoExecuting(false)
    })
  }, [])

  return { isCkoExecuting }
}
